import React, { useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardContainer from '../../components/Wizard/WizardContainer';
import CourierAgreementModal from '../../components/CourierAgreementModal';
import { ApplicationStatus, FitAssessmentAnswers } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking, useStepTimer, useModalTracking, useAnalytics } from '../../hooks/useAnalytics';
import { courierApiUrl } from '../../config';
import {
  acceptCourierAgreement,
  getApplicantV2ErrorMessage,
  getApplicantV2State,
  loadApplicantV2Session,
  submitFitAssessment,
} from '../../utils/applicantV2';

interface FitAssessmentProps {
  applicationStatus: ApplicationStatus;
}

const FitAssessment: React.FC<FitAssessmentProps> = ({ applicationStatus }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('email') || '';

  const [answers, setAnswers] = useState<Partial<FitAssessmentAnswers>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Analytics
  usePageTracking('Fit Assessment');
  useWizardStepTracking('fit-assessment', 1, 7);
  const { getTimeSpent } = useStepTimer();
  const { onOpen: onModalOpen, onClose: onModalClose } = useModalTracking('courier_agreement');
  const { trackFitAssessmentComplete, trackAgreementAccepted, trackWizardStepComplete, trackAPIError } = useAnalytics();
  const agreementViewedRef = useRef(false);

  const handleAnswer = (questionId: keyof FitAssessmentAnswers, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setError('');
  };

  const isComplete = () => {
    return (
      answers.daysPerWeek &&
      answers.commitmentLevel &&
      answers.workStyle &&
      answers.motivation &&
      answers.hasReliableTransportation !== undefined &&
      agreedToTerms
    );
  };

  const handleSubmit = async () => {
    if (!isComplete()) {
      setError('Please answer all questions and agree to the courier agreement before continuing.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const v2Session = loadApplicantV2Session();
      if (v2Session) {
        if (applicationStatus.fitAssessmentStatus !== 'COMPLETED') {
          try {
            await submitFitAssessment(v2Session, answers as FitAssessmentAnswers);
          } catch (fitError: any) {
            if (fitError.response?.status !== 409) throw fitError;
            const latestState = await getApplicantV2State(v2Session);
            if (latestState.fitAssessmentStatus !== 'COMPLETED') throw fitError;
          }
        }
        if (applicationStatus.agreementStatus !== 'ACCEPTED') {
          try {
            await acceptCourierAgreement(v2Session);
          } catch (agreementError: any) {
            if (agreementError.response?.status !== 409) throw agreementError;
            const latestState = await getApplicantV2State(v2Session);
            if (latestState.agreementStatus !== 'ACCEPTED') throw agreementError;
          }
        }

        trackFitAssessmentComplete({ answers: answers as FitAssessmentAnswers });
        trackAgreementAccepted({ agreementViewed: agreementViewedRef.current, timeToAccept: getTimeSpent() });
        trackWizardStepComplete({ stepName: 'fit-assessment', timeOnStep: getTimeSpent(), completionStatus: 'success' });
        navigate('/wizard/local-knowledge');
        return;
      }

      const response = await axios.post(courierApiUrl('/application/fit-assessment'), {
        token,
        answers: answers as FitAssessmentAnswers,
        agreeTime: Math.floor(Date.now() / 1000)
      });

      if (response.data.success) {
        // Track completion
        trackFitAssessmentComplete({ answers: answers as FitAssessmentAnswers });
        trackAgreementAccepted({ agreementViewed: agreementViewedRef.current, timeToAccept: getTimeSpent() });
        trackWizardStepComplete({ stepName: 'fit-assessment', timeOnStep: getTimeSpent(), completionStatus: 'success' });
        // Navigate to next step
        navigate(`/wizard/local-knowledge?token=${token}`);
      } else {
        setError(response.data.error || 'Failed to submit assessment');
      }
    } catch (err: any) {
      console.error('Fit assessment error:', err);
      trackAPIError({ endpoint: '/api/application/fit-assessment', errorCode: err.response?.status || 'unknown', errorMessage: err.message });
      setError(getApplicantV2ErrorMessage(err, 'An error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardContainer
      currentStepId="fit-assessment"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-3xl mx-auto">
        <div className="card p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-charcoal mb-2">
            Quick Questions
          </h1>
          <p className="text-gray-600 mb-8">
            Quick 1-3 minute questionnaire to help us understand your availability and preferences.
          </p>

          <div className="space-y-8">
            {/* Question 1: Days per week */}
            <div>
              <label className="block text-charcoal font-semibold mb-3">
                1. How many days per week do you plan to drive?
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {['1', '2-3', '4-5', '6+'].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswer('daysPerWeek', option)}
                    className={`
                      selection-card p-4 font-semibold
                      ${answers.daysPerWeek === option
                        ? 'selection-card-selected'
                        : 'text-charcoal'}
                    `}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 2: Commitment level */}
            <div>
              <label className="block text-charcoal font-semibold mb-3">
                2. If you commit to a shift, how likely are you to show up?
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((level) => (
                  <button
                    key={level}
                    onClick={() => handleAnswer('commitmentLevel', level)}
                    className={`
                      selection-card p-4
                      ${answers.commitmentLevel === level
                        ? 'selection-card-selected'
                        : 'text-charcoal'}
                    `}
                  >
                    <div className="text-2xl font-bold">{level}</div>
                    <div className="text-xs mt-1">
                      {level === 1 && 'Sometimes'}
                      {level === 2 && 'Usually'}
                      {level === 3 && 'Often'}
                      {level === 4 && 'Always'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Question 3: Work style */}
            <div>
              <label className="block text-charcoal font-semibold mb-3">
                3. Which best describes how you like to work?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'busy-fast-paced', label: 'Busy & fast-paced' },
                  { value: 'steady-predictable', label: 'Steady & predictable' },
                  { value: 'light-flexible', label: 'Light & flexible' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer('workStyle', option.value)}
                    className={`
                      selection-card w-full p-4 text-left font-semibold
                      ${answers.workStyle === option.value
                        ? 'selection-card-selected'
                        : 'text-charcoal'}
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 4: Motivation */}
            <div>
              <label className="block text-charcoal font-semibold mb-3">
                4. What motivated you to become a Valley Eats courier?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'extra-income', label: 'Extra income' },
                  { value: 'flexible-hours', label: 'Flexible hours' },
                  { value: 'full-time', label: 'Full-time work' },
                  { value: 'supporting-local', label: 'Supporting local businesses' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer('motivation', option.value)}
                    className={`
                      selection-card w-full p-4 text-left font-semibold
                      ${answers.motivation === option.value
                        ? 'selection-card-selected'
                        : 'text-charcoal'}
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 5: Reliable transportation */}
            <div>
              <label className="block text-charcoal font-semibold mb-3">
                5. Do you have reliable transportation?
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' }
                ].map((option) => (
                  <button
                    key={option.label}
                    onClick={() => handleAnswer('hasReliableTransportation', option.value)}
                    className={`
                      selection-card p-4 font-semibold
                      ${answers.hasReliableTransportation === option.value
                        ? 'selection-card-selected'
                        : 'text-charcoal'}
                    `}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Courier Agreement Section */}
          <div className="soft-panel mt-8">
            <h3 className="text-lg font-semibold text-charcoal mb-3">Courier Agreement</h3>
            <p className="text-sm text-gray-600 mb-4">
              Before continuing, please review and agree to our courier agreement.
            </p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  setError('');
                }}
                className="mt-1 w-5 h-5 text-logo-green border-gray-300 rounded focus:ring-logo-green"
              />
              <span className="text-sm text-charcoal">
                I have read and agree to the{' '}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    agreementViewedRef.current = true;
                    onModalOpen('agreement_checkbox');
                    setShowModal(true);
                  }}
                  className="text-logo-green hover:text-shadow-green font-semibold underline"
                >
                  Valley Eats Courier Agreement
                </button>
              </span>
            </label>
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-6 bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4">
              {error}
            </div>
          )}

          {/* Submit button */}
          <div className="mt-8">
            <button
              onClick={handleSubmit}
              disabled={!isComplete() || isSubmitting}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Continue to Next Step'}
            </button>
          </div>

          {/* Progress indicator */}
          <div className="mt-4 text-center text-sm text-gray-500">
            {Object.keys(answers).length} of 5 questions answered
          </div>
        </div>
      </div>

      {/* Courier Agreement Modal */}
      {showModal && <CourierAgreementModal onClose={() => { onModalClose('button'); setShowModal(false); }} />}
    </WizardContainer>
  );
};

export default FitAssessment;
